import { useState } from "react";
import { Head } from "@inertiajs/react";
import AppLayout from "@/components/AppLayout";
import { Tabs, TabsContent } from "@/components/ui/Tabs";
import ReviewTabsList from "../TopDown/components/ReviewTabsList";
import { Button } from "@/components/ui/Button";
import PlanHeader from "../components/PlanHeader";
import ReviewSubmitPopover from "../TopDown/components/ReviewSubmitPopover";
import ReviewTaskCard from "../TopDown/components/ReviewTaskCard";
import CommentCard, { type CommentProps } from "../TopDown/components/CommentCard";
import CommentComposer from "../TopDown/components/CommentComposer";
import SubmissionCard, { type SubmissionProps } from "../TopDown/components/SubmissionCard";
import ByMonthDiffTable from "./components/ByMonthDiffTable";
import { monthlyChangeCount, monthlySummaryText, type ByMonthDiff } from "./components/byMonthDiff";
import type { Row, Channel } from "./byMonthTypes";
import { type Person, type Task } from "@/pages/Tasks/task";
import { plan_bottom_up_by_month_channel_path, tasks_path } from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";

interface Props {
  plan: { id: string; name: string; slug: string; begin_date: string; end_date: string; currency: string | null; revenue_target: number; org_node: { id: string; name: string } };
  months: string[];
  rows: Row[];
  current_channel: Channel;
  // null when the task was submitted with no draft open (nothing changed).
  diff: ByMonthDiff | null;
  review_task: Task;
  people: Person[];
  submission: SubmissionProps;
  comments: CommentProps[];
}

// The reviewer's page for one channel's monthly plan submission, GitHub PR
// layout like Plan/BottomUp/ProductPricingsReview: the submitted draft
// read-only with green/red marks against the published cells, the
// submission message as a timeline comment, the task as a sidebar. The
// verdict lives in the app header's Submit review popover.
export default function ByMonthReview({ plan, months, rows, current_channel, diff, review_task, people, submission, comments }: Props) {
  const market = useMarket();
  const [changesOnly, setChangesOnly] = useState(true);
  const showChangesOnly = changesOnly && diff != null;
  const showPath = plan_bottom_up_by_month_channel_path(market.slug, plan.slug, current_channel.slug);

  return (
    <AppLayout
      header={<PlanHeader name={plan.name} orgNodeName={plan.org_node.name} revenueTarget={plan.revenue_target} currency={plan.currency} beginDate={plan.begin_date} endDate={plan.end_date} />}
      headerActions={submission.can_review && (
        <ReviewSubmitPopover
          submissionId={submission.id}
          planName={`${current_channel.name} monthly plan`}
          revision={diff != null}
          approveBackTo={showPath}
          sendBackBackTo={tasks_path()}
        />
      )}
    >
      <Head title={`${current_channel.name} monthly plan — Review`} />
      <div className="mb-4 rounded-lg border px-4 py-2.5 text-sm text-foreground" style={{ background: "var(--info-surface)", borderColor: "var(--border)" }}>
        <span className="font-semibold">{current_channel.name} · Monthly Plan.</span>{" "}
        {diff ? monthlySummaryText(diff.summary) : "No changes against the published plan"}.
        {diff?.summary.any_changes && " In the Plan changes tab, green is what this submission sets and red is the published value it replaces."}
      </div>
      <Tabs defaultValue="conversation">
        <ReviewTabsList commentCount={comments.length} changeCount={diff ? monthlyChangeCount(diff.summary) : null} />
        <TabsContent value="conversation">
          <div className="flex items-start gap-4">
            <div className="flex min-w-0 flex-col gap-4 lg:w-3/5">
              <SubmissionCard submission={submission} />
              {comments.map((comment) => <CommentCard key={comment.id} comment={comment} submissionId={submission.id} />)}
              <CommentComposer submissionId={submission.id} />
            </div>
            <ReviewTaskCard task={review_task} people={people} />
          </div>
        </TabsContent>
        <TabsContent value="changes">
          <div className="overflow-hidden rounded-md border bg-card">
            <div className="flex items-center justify-between border-b px-4 py-2 text-[12px] text-muted-foreground">
              <span>{current_channel.name}</span>
              {diff && (
                <Button variant="ghost" size="sm" className="h-7 text-[12px]" onClick={() => setChangesOnly((v) => !v)}>
                  {changesOnly ? "Show all rows" : "Show changes only"}
                </Button>
              )}
            </div>
            <ByMonthDiffTable rows={rows} months={months} diff={diff} changesOnly={showChangesOnly} emptyMessage={showChangesOnly ? "No changed rows." : `No products for ${current_channel.name}.`} />
          </div>
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
